import React, { useEffect, useMemo, useState } from "react";
import { api, waLink } from "@/lib/api";
import { MapPin, BedDouble, Ruler, Filter, ShieldCheck, ArrowRight } from "lucide-react";

const LOCATIONS = ["All", "OMR", "ECR", "Velachery", "Anna Nagar", "Adyar", "Tambaram", "Porur", "Sholinganallur", "T. Nagar"];
const TYPES = [
  { v: "All", l: "All Types" },
  { v: "apartment", l: "Apartment" },
  { v: "independent_house", l: "Independent House" },
  { v: "villa", l: "Villa" },
  { v: "commercial", l: "Commercial" },
  { v: "plot", l: "Plot" },
];
const BUDGETS = [
  { v: "All", l: "Any Budget" },
  { v: "below_25k", l: "Below ₹25,000" },
  { v: "25k_50k", l: "₹25,000 – ₹50,000" },
  { v: "50k_1l", l: "₹50,000 – ₹1 lakh" },
  { v: "1l_plus", l: "₹1 lakh+" },
];
const BHK = ["All", "1BHK", "2BHK", "3BHK", "4BHK"];
const STATUSES = [
  { v: "All", l: "All Listings" },
  { v: "for_rent", l: "For Rent" },
  { v: "for_sale", l: "For Sale" },
  { v: "managed", l: "Managed by Hi-Tech" },
];

export default function Properties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ location: "All", property_type: "All", budget_band: "All", bhk: "All", status: "All" });

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const params = {};
        Object.entries(f).forEach(([k, v]) => { if (v && v !== "All") params[k] = v; });
        const { data } = await api.get("/properties", { params });
        setItems(data);
      } finally { setLoading(false); }
    };
    run();
  }, [f]);

  const change = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div data-testid="page-properties">
      <section className="bg-brand-navy text-white">
        <div className="container-x py-20 md:py-24">
          <div className="eyebrow !text-brand-cyan">Properties</div>
          <h1 className="mt-3 font-display text-4xl md:text-6xl font-bold leading-tight max-w-4xl">
            Verified rental and sale properties across Chennai.
          </h1>
          <p className="mt-4 text-white/80 max-w-2xl">Every listing is inspected and verified. Many are actively managed by Hi-Tech Realty — inspection reports available on request.</p>
        </div>
      </section>

      <section className="container-x -mt-10">
        <div className="bg-white border border-brand-border p-5 md:p-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          <FilterSelect label="Location" value={f.location} onChange={change("location")} options={LOCATIONS.map((v) => ({ v, l: v }))} testid="filter-location" />
          <FilterSelect label="Type" value={f.property_type} onChange={change("property_type")} options={TYPES} testid="filter-type" />
          <FilterSelect label="Budget" value={f.budget_band} onChange={change("budget_band")} options={BUDGETS} testid="filter-budget" />
          <FilterSelect label="BHK" value={f.bhk} onChange={change("bhk")} options={BHK.map((v) => ({ v, l: v }))} testid="filter-bhk" />
          <FilterSelect label="Status" value={f.status} onChange={change("status")} options={STATUSES} testid="filter-status" />
        </div>
      </section>

      <section className="section-pad !pt-12">
        {loading ? (
          <div className="text-brand-navy">Loading properties…</div>
        ) : items.length === 0 ? (
          <div className="card-flat p-10 text-center">
            <div className="font-display text-2xl text-brand-navy font-bold">No matching properties</div>
            <p className="mt-2 text-sm text-brand-slate/70">Try widening the filters, or chat with us for off-listing options.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-navy">
        <Filter size={12} className="text-brand-cyan" /> {label}
      </span>
      <select data-testid={testid} value={value} onChange={onChange}
        className="mt-2 w-full bg-white border border-brand-border px-3 py-2.5 text-sm focus:outline-none focus:border-brand-cyan">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function PropertyCard({ p }) {
  const priceLabel = p.status === "for_sale"
    ? `₹${(p.price / 100000).toFixed(2)} L`
    : p.status === "managed"
    ? "Managed"
    : `₹${p.price.toLocaleString("en-IN")}/mo`;
  return (
    <article data-testid={`property-card-${p.id}`} className="card-flat overflow-hidden flex flex-col group">
      <div className="relative">
        <img src={p.image_url} alt={p.title} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
        {p.verified && (
          <div className="absolute top-3 left-3 bg-white text-brand-navy text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 flex items-center gap-1">
            <ShieldCheck size={12} className="text-brand-cyan" /> Verified
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-navy/85 to-transparent p-4">
          <div className="text-white font-display text-xl font-bold">{p.title}</div>
          <div className="text-white/80 text-xs flex items-center gap-1 mt-1"><MapPin size={12} /> {p.location} · {p.address}</div>
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-4 text-xs text-brand-slate/80">
          {p.bhk && <span className="flex items-center gap-1"><BedDouble size={14}/> {p.bhk}</span>}
          {p.area_sqft && <span className="flex items-center gap-1"><Ruler size={14}/> {p.area_sqft} sqft</span>}
          {p.furnishing && <span className="text-brand-cyan font-semibold">{p.furnishing}</span>}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="font-display text-2xl font-bold text-brand-navy">{priceLabel}</div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-cyan">
            {p.status === "for_rent" ? "For Rent" : p.status === "for_sale" ? "For Sale" : "Managed"}
          </div>
        </div>
        {p.description && <p className="mt-3 text-xs text-brand-slate/70 leading-relaxed">{p.description}</p>}
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(p.badges || []).map((b) => (
            <span key={b} className="text-[10px] uppercase tracking-[0.18em] font-semibold text-brand-navy border border-brand-border px-2 py-1">{b}</span>
          ))}
        </div>
        <div className="mt-5 flex gap-2">
          <a
            data-testid={`property-whatsapp-${p.id}`}
            href={waLink(`Hi Hi-Tech Realty, I am interested in: ${p.title} (${p.location}).`)}
            target="_blank" rel="noreferrer"
            className="btn-whatsapp flex-1 !py-2.5 !text-[10px]"
          >WhatsApp Enquiry</a>
          <a
            data-testid={`property-details-${p.id}`}
            href={waLink(`Hi, please share full details and inspection report of ${p.title}.`)}
            target="_blank" rel="noreferrer"
            className="btn-ghost-navy flex-1 !py-2.5 !text-[10px]"
          >View Details <ArrowRight size={12} /></a>
        </div>
      </div>
    </article>
  );
}
